import { useEffect, useMemo, useState } from "react";
import { X, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/shopify";

export default function ProductDetailModal({ product, onClose }) {
  const { addItem, mutating } = useCart();
  const variants = useMemo(
    () => product.variants?.edges?.map((e) => e.node) || [],
    [product]
  );
  const images = useMemo(() => {
    const fromEdges = product.images?.edges?.map((e) => e.node) || [];
    if (fromEdges.length > 0) return fromEdges;
    return product.featuredImage ? [product.featuredImage] : [];
  }, [product]);

  // Init selected options to first available variant
  const initialOptions = useMemo(() => {
    const target = variants.find((v) => v.availableForSale) || variants[0];
    const map = {};
    target?.selectedOptions?.forEach((o) => {
      map[o.name] = o.value;
    });
    return map;
  }, [variants]);

  const [selectedOptions, setSelectedOptions] = useState(initialOptions);
  const [imgIndex, setImgIndex] = useState(0);
  const [added, setAdded] = useState(false);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const activeVariant = useMemo(() => {
    return (
      variants.find((v) =>
        v.selectedOptions.every((o) => selectedOptions[o.name] === o.value)
      ) || variants[0]
    );
  }, [variants, selectedOptions]);

  const handleAdd = async () => {
    if (!activeVariant) return;
    await addItem(activeVariant, product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const handleOption = (name, value) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-navy-deep/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={product.title}
      data-testid="product-detail-modal"
    >
      <div
        className="relative bg-white rounded-2xl w-full max-w-5xl shadow-2xl grid lg:grid-cols-2 overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow ring-1 ring-gray-200"
          data-testid="product-detail-close"
        >
          <X size={18} />
        </button>

        {/* Image gallery */}
        <div className="relative bg-cream aspect-square lg:aspect-auto">
          {images.length > 0 ? (
            <img
              src={images[imgIndex].url}
              alt={images[imgIndex].altText || product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-navy-deep/30">
              <ShoppingBag size={64} />
            </div>
          )}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() =>
                  setImgIndex((i) => (i - 1 + images.length) % images.length)
                }
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 hover:bg-white shadow inline-flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setImgIndex((i) => (i + 1) % images.length)}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 hover:bg-white shadow inline-flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 w-1.5 rounded-full ${
                      i === imgIndex ? "bg-navy" : "bg-white/80 ring-1 ring-navy/30"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Details */}
        <div className="p-8 lg:p-10 flex flex-col overflow-y-auto max-h-[80vh]">
          {product.productType && (
            <p className="font-display tracking-[0.22em] text-[11px] uppercase text-green-brand">
              {product.productType}
            </p>
          )}
          <h2 className="mt-1 h-display text-3xl text-navy leading-tight">
            {product.title}
          </h2>
          <span className="mt-3 inline-block h-[3px] w-16 bg-green-brand" />

          <p className="mt-5 text-2xl font-semibold text-navy">
            {formatPrice(activeVariant?.price || product.priceRange?.minVariantPrice)}
            {activeVariant?.compareAtPrice && (
              <span className="ml-3 text-base text-navy-deep/50 line-through font-normal">
                {formatPrice(activeVariant.compareAtPrice)}
              </span>
            )}
          </p>

          {product.descriptionHtml && (
            <div
              className="mt-6 text-navy-deep/80 leading-relaxed text-sm prose prose-sm max-w-none"
              // Shopify-controlled HTML
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          )}

          {/* Variant options */}
          {product.options
            ?.filter((o) => !(o.values.length === 1 && o.values[0] === "Default Title"))
            .map((opt) => (
              <div key={opt.id} className="mt-6">
                <p className="font-display tracking-widest text-xs uppercase text-navy">
                  {opt.name}:{" "}
                  <span className="text-navy-deep/70 normal-case tracking-normal">
                    {selectedOptions[opt.name]}
                  </span>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {opt.values.map((val) => {
                    const isSelected = selectedOptions[opt.name] === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => handleOption(opt.name, val)}
                        className={`px-3 py-1.5 text-sm rounded-md ring-1 transition-colors ${
                          isSelected
                            ? "bg-navy text-white ring-navy"
                            : "bg-white text-navy ring-gray-300 hover:ring-green-brand"
                        }`}
                        data-testid={`option-${opt.name}-${val}`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-1.5">
              {product.tags.map((t) => (
                <span
                  key={t}
                  className="inline-block text-[10px] uppercase tracking-wider font-display text-navy-deep/70 bg-gray-soft px-2 py-0.5 rounded"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!activeVariant?.availableForSale || mutating}
              className="btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="product-detail-add-to-cart"
            >
              <ShoppingBag size={18} />
              {!activeVariant?.availableForSale
                ? "Sold Out"
                : added
                ? "Added!"
                : mutating
                ? "Adding…"
                : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
